<script>
import { h } from 'vue'
import { getFullCloudfrontImageUrl, organisationProfileImage } from 'give-a-day-shared/utils/s3Path.mjs'
import { IMAGE_FORMATS } from 'give-a-day-shared/utils/imageFormats.mjs'
import { isSet } from 'give-a-day-shared/utils/isSet.mjs'
import { removeAllHtmlTags, safeTrim } from 'give-a-day-shared/utils/text.mjs'
import { STATUSES, statusEquals } from 'give-a-day-shared/data/enums/oneDayVacancyEnums.mjs'
import { getCountryIsoForCountryId } from 'give-a-day-shared/data/locales.mjs'
import { useCustomPageMetaUrl } from '@/composables/useCustomPageMetaUrl.js'

export default {
  name: 'GoogleJobPostingJsonOneDayVacancy',
  props: {
    oneDayVacancy: { type: Object, required: true },
    organisation: { type: Object, required: true },
  },
  computed: {
    allDataLooksGood () {
      return this.oneDayVacancy && this.oneDayVacancy.id && statusEquals(this.oneDayVacancy.status, STATUSES.ACTIVE) && this.organisation && this.organisation.id && this.oneDayVacancy.address &&
        this.oneDayVacancy.address.countryId && this.oneDayVacancy.address.freetextMunicipality && this.oneDayVacancy.address.freetextZipcode && this.oneDayVacancy.address.freetextStreet
    },
  },
  methods: {
    formatDate (date) {
      if (typeof date === 'string' || !isSet(date)) {
        return date
      }
      return date.toISOString()
    },
  },
  render () {
    if (!this.allDataLooksGood) {
      return h('script') // Render empty script. Nothing else to do
    }

    const { getCustomCanonicalUrl } = useCustomPageMetaUrl()

    let oneDayVacancy = this.oneDayVacancy
    let address = oneDayVacancy.address
    let streetAddress = address.streetNumber ? `${address.freetextStreet} ${address.streetNumber}` : address.freetextStreet
    let description = `${this.$gadTranslate('page_one_day_vacancy_detail_description_title')}: ${safeTrim(removeAllHtmlTags(oneDayVacancy.description))}<br>`
    let oneDayVacancyUrl = null
    try {
      oneDayVacancyUrl = getCustomCanonicalUrl(oneDayVacancy.id, oneDayVacancy.nameLocalisations, oneDayVacancy.address.countryId, 'oneDayVacancy')
    } catch (e) {
      // If the above throws an error, fallback to the regular URL generation method.
      oneDayVacancyUrl = this.$oneDayVacancyUrl(oneDayVacancy)
    }
    let json = {
      '@context': 'https://schema.org/',
      '@type': 'JobPosting',
      title: oneDayVacancy.name,
      url: oneDayVacancyUrl,
      datePosted: this.formatDate(oneDayVacancy.activityDate),
      validThrough: this.formatDate(oneDayVacancy.activityDate),
      description,
      hiringOrganization: {
        '@type': 'Organization',
        name: this.organisation.name,
        legalName: this.organisation.name,
      },
      jobLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          streetAddress,
          addressLocality: address.freetextMunicipality,
          postalCode: address.freetextZipcode,
        },
      },
      employmentType: 'VOLUNTEER',
      identifier: {
        '@type': 'PropertyValue',
        name: 'One-Day Vacancy ID',
        value: oneDayVacancy.id,
      },
    }
    if (address && address.lat && address.lng) {
      json.jobLocation.address.latitude = address.lat
      json.jobLocation.address.longitude = address.lng
      json.jobLocation.geo = {
        '@type': 'GeoCoordinates',
        latitude: address.lat,
        longitude: address.lng,
      }
    }
    if (this.organisation.websiteUrl) {
      json.hiringOrganization.sameAs = this.organisation.websiteUrl
    }
    if (this.organisation.avatarExistsOnS3) {
      let orgProfile = IMAGE_FORMATS.organisation_profile
      json.hiringOrganization.logo = getFullCloudfrontImageUrl(organisationProfileImage(this.organisation.id), orgProfile.widths[orgProfile.widths.length - 1], 'png', this.organisation.updated)
    }
    try {
      const countryIso = getCountryIsoForCountryId(address.countryId)
      if (countryIso) {
        json.jobLocation.address.addressCountry = countryIso.toUpperCase()
      }
    } catch (e) {
      // If the countryId is unsupported or unknown, skip addressCountry
    }
    return h('script', { type: 'application/ld+json', innerHTML: JSON.stringify(json) })
  },
}
</script>
